import { Analytics } from '@vercel/analytics/react';
import jsPDF from 'jspdf';
import { Download } from 'lucide-react';
import React, { useEffect, useState } from 'react';

function Resume() {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setTimeout(() => setIsLoaded(true), 100);
  }, []);

  const handlePrint = async () => {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    // Load Inter fonts
    const loadFont = async (url, fontName, fontStyle) => {
      const response = await fetch(url);
      const buffer = await response.arrayBuffer();
      const base64 = btoa(
        new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
      );
      const fileName = `${fontName}-${fontStyle}.ttf`;
      pdf.addFileToVFS(fileName, base64);
      pdf.addFont(fileName, fontName, fontStyle);
    };

    await Promise.all([
      loadFont('/fonts/Inter-Regular.ttf', 'Inter', 'normal'),
      loadFont('/fonts/Inter-Bold.ttf', 'Inter', 'bold'),
    ]);

    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    const mainColWidth = contentWidth * 0.65;
    const sideColWidth = contentWidth * 0.30;
    const sideColX = margin + mainColWidth + contentWidth * 0.05;

    let y = margin;

    // Helper functions
    const setFont = (style, size) => {
      pdf.setFont('Inter', style);
      pdf.setFontSize(size);
    };

    const setColor = (hex) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      pdf.setTextColor(r, g, b);
    };

    const wrapText = (text, maxWidth) => {
      return pdf.splitTextToSize(text, maxWidth);
    };

    // Header
    setFont('bold', 22);
    setColor('#1d1d1f');
    pdf.text('YOUSEF NASSAR', pageWidth / 2, y, { align: 'center' });
    y += 7;

    setFont('normal', 10);
    setColor('#666666');
    pdf.text('Software Engineer & Architect', pageWidth / 2, y, { align: 'center' });
    y += 6;

    // Contact info with clickable links
    setFont('normal', 9);
    const contactParts = [
      { text: 'n.yossif@gmail.com', link: 'mailto:n.yossif@gmail.com' },
      { text: '  |  ' },
      { text: '+972 50 577 1645' },
      { text: '  |  ' },
      { text: 'Haifa' },
      { text: '  |  ' },
      { text: 'Website', link: 'https://ynassar.com' },
      { text: '  |  ' },
      { text: 'LinkedIn', link: 'https://www.linkedin.com/in/yousef-nassar-314a3b5b/' },
    ];

    // Calculate total width
    let totalWidth = 0;
    contactParts.forEach(part => {
      totalWidth += pdf.getTextWidth(part.text);
    });

    // Start position to center
    let xPos = (pageWidth - totalWidth) / 2;

    contactParts.forEach(part => {
      if (part.link) {
        setColor('#0066cc');
        pdf.textWithLink(part.text, xPos, y, { url: part.link });
      } else {
        setColor('#1d1d1f');
        pdf.text(part.text, xPos, y);
      }
      xPos += pdf.getTextWidth(part.text);
    });
    y += 4;

    // Divider line
    setColor('#e0e0e0');
    pdf.setLineWidth(0.3);
    pdf.line(margin, y, pageWidth - margin, y);
    y += 8;

    const startY = y;

    // === LEFT COLUMN: EXPERIENCE ===
    setFont('bold', 11);
    setColor('#1d1d1f');
    pdf.text('EXPERIENCE', margin, y);
    y += 6;

    const jobs = [
      {
        title: 'Tech Lead, Data & AI Infra Platform',
        company: 'Melio',
        location: 'Tel Aviv',
        period: '2026 - Present',
        bullets: [
          'Lead the Data & AI Infra Platform team, owning the platform foundations for agentic and data workloads',
          'Leading design and adoption of agentic infrastructure on AWS Bedrock AgentCore across engineering teams',
          'Driving observability for AI agents and services via OpenTelemetry instrumentation integrated with Datadog',
          'Leading data governance for business data enrichment and analytics pipelines',
        ],
      },
      {
        title: 'Co-Founder',
        company: 'Infragate',
        location: 'Haifa',
        period: '2025 - Present',
        link: 'https://infragate.ai',
        bullets: [
          'Built ShareCube (sharecube.io), a publishing and collaboration service where AI agents publish docs over MCP',
          'ShareCube: selection-anchored comments, editable versioned artifacts, org roles, scoped API keys and search',
          'Built Cairn (cairn.infragate.co), an OpenTelemetry observability and evals platform for AI coding agents',
          'Cairn ingests all 3 OTLP signals via Rust Lambdas into a hot DynamoDB path and a cold Apache Iceberg lake',
          'Built LLM-as-judge evals: 5 built-in evaluators on Bedrock, benchmarked on a 59-unit frozen set',
          'Built no-ops platform to create and deploy serverless MCP servers entirely through UI, pay-as-you-go',
        ],
      },
      {
        title: 'Software Engineer',
        company: 'Meta',
        location: 'Tel Aviv',
        period: '2021 - 2025',
        bullets: [
          'Led ticket automation infrastructure reducing manual review by 65%, saving thousands of hours monthly',
          'Architected ML-powered recommendation systems for Instagram & Facebook Shops, achieving 25% engagement increase',
          'Spearheaded Similar Items & More from This Shop features with breakthrough ML models',
          'Conducted extensive A/B testing optimizing user engagement and conversion rates',
        ],
      },
      {
        title: 'Software Engineer',
        company: 'Amazon',
        location: 'Haifa',
        period: '2019 - 2021',
        bullets: [
          'Designed end-to-end architecture and led the Browse Node Scoping project for Alexa Shopping',
          'Architected scalable infrastructure using AWS CDK with load balancers and Fargate',
          'Led cross-team technical initiatives establishing microservices best practices',
          'Drove architectural decisions, mentoring engineers on system design',
        ],
      },
      {
        title: 'Software Engineer',
        company: 'GE Healthcare',
        location: 'Haifa',
        period: '2018 - 2019',
        bullets: [
          'Led backend architecture for AI assistant deployed in US hospitals',
          'Introduced AWS AppSync with GraphQL, improving API development efficiency',
          'Optimized AWS Lambda for improved scalability and reduced costs',
        ],
      },
      {
        title: 'Software Engineer',
        company: 'Outbrain',
        location: 'Netanya',
        period: '2016 - 2018',
        bullets: [
          'Built real-time fraud detection reducing fraudulent events by 40%',
          'Processed millions of daily click events with Prometheus/Grafana monitoring',
          'Established CI/CD pipelines improving deployment frequency by 60%',
        ],
      },
      {
        title: 'Software Engineer Intern',
        company: 'Microsoft',
        location: 'Herzliya',
        period: '2014 - 2016',
        bullets: [
          'Built UI components for data visualization and backend retention policies for Azure',
        ],
      },
    ];

    jobs.forEach((job) => {
      // Job title and period on same line
      setFont('bold', 9);
      setColor('#1d1d1f');
      pdf.text(job.title, margin, y);

      setFont('normal', 8);
      setColor('#666666');
      pdf.text(job.period, margin + mainColWidth - 2, y, { align: 'right' });
      y += 4;

      // Company
      setFont('normal', 8);
      setColor('#0066cc');
      const companyText = `${job.company} · ${job.location}`;
      if (job.link) {
        pdf.textWithLink(companyText, margin, y, { url: job.link });
      } else {
        pdf.text(companyText, margin, y);
      }
      y += 4;

      // Bullets
      setFont('normal', 8);
      setColor('#424245');
      job.bullets.forEach((bullet) => {
        const lines = wrapText(`• ${bullet}`, mainColWidth - 4);
        lines.forEach((line, i) => {
          pdf.text(i === 0 ? line : `  ${line}`, margin + 2, y);
          y += 3.5;
        });
      });
      y += 3;
    });

    // === RIGHT COLUMN ===
    let sideY = startY;

    // Skills
    setFont('bold', 10);
    setColor('#1d1d1f');
    pdf.text('SKILLS', sideColX, sideY);
    sideY += 5;

    const skills = [
      { label: 'Leadership', items: 'System Architecture, Technical Strategy, Cross-functional Collaboration' },
      { label: 'Backend', items: 'AWS, Microservices, Python, Java, Node.js' },
      { label: 'Frontend', items: 'React, React Native, TypeScript, Expo' },
      { label: 'AI & ML', items: 'ML Systems, NLP, Deep Learning' },
      { label: 'DevOps', items: 'CI/CD, Docker, Kubernetes' },
    ];

    skills.forEach((skill) => {
      setFont('bold', 8);
      setColor('#1d1d1f');
      pdf.text(skill.label, sideColX, sideY);
      sideY += 3.5;

      setFont('normal', 7.5);
      setColor('#424245');
      const lines = wrapText(skill.items, sideColWidth);
      lines.forEach((line) => {
        pdf.text(line, sideColX, sideY);
        sideY += 3;
      });
      sideY += 2;
    });

    sideY += 4;

    // Education
    setFont('bold', 10);
    setColor('#1d1d1f');
    pdf.text('EDUCATION', sideColX, sideY);
    sideY += 5;

    setFont('bold', 8);
    pdf.text('BSc. Computer Science', sideColX, sideY);
    sideY += 3.5;

    setFont('normal', 7.5);
    setColor('#0066cc');
    pdf.text('Technion - Israel Institute of Technology', sideColX, sideY);
    sideY += 3.5;

    setColor('#666666');
    pdf.text('2012 - 2017', sideColX, sideY);
    sideY += 8;

    // Certifications
    setFont('bold', 10);
    setColor('#1d1d1f');
    pdf.text('CERTIFICATIONS', sideColX, sideY);
    sideY += 5;

    // Deep Learning Specialization
    setFont('bold', 7.5);
    setColor('#0066cc');
    pdf.textWithLink('Deep Learning Specialization', sideColX, sideY, { url: 'https://coursera.org/share/475756e9f3fe76ab11b67507ae5aa5c1' });
    sideY += 3;
    setFont('normal', 6.5);
    setColor('#86868b');
    pdf.text('DeepLearning.AI · Coursera · Sept 2020', sideColX, sideY);
    sideY += 3;
    setFont('normal', 6.5);
    setColor('#636366');
    const dlCourses = ['Neural Networks and Deep Learning', 'Improving Deep Neural Networks', 'Structuring ML Projects', 'Convolutional Neural Networks', 'Sequence Models'];
    dlCourses.forEach((course) => {
      pdf.text(`· ${course}`, sideColX + 2, sideY);
      sideY += 2.5;
    });
    sideY += 2;

    // NLP Specialization
    setFont('bold', 7.5);
    setColor('#0066cc');
    pdf.textWithLink('NLP Specialization', sideColX, sideY, { url: 'https://www.coursera.org/account/accomplishments/verify/SWGGZDT49ZZN' });
    sideY += 3;
    setFont('normal', 6.5);
    setColor('#86868b');
    pdf.text('DeepLearning.AI · Coursera · Dec 2020', sideColX, sideY);
    sideY += 4;

    // Big Data with Scala and Spark
    setFont('bold', 7.5);
    setColor('#0066cc');
    pdf.textWithLink('Big Data Analysis with Scala and Spark', sideColX, sideY, { url: 'https://www.coursera.org/account/accomplishments/verify/9L8NP7Q7F2QZ' });
    sideY += 3;
    setFont('normal', 6.5);
    setColor('#86868b');
    pdf.text('EPFL · Coursera · Feb 2020', sideColX, sideY);
    sideY += 4;

    // Functional Programming in Scala
    setFont('bold', 7.5);
    setColor('#0066cc');
    pdf.textWithLink('Functional Programming in Scala', sideColX, sideY, { url: 'https://www.coursera.org/account/accomplishments/verify/KBL5C6Q3NXNG' });
    sideY += 3;
    setFont('normal', 6.5);
    setColor('#86868b');
    pdf.text('EPFL · Coursera · Apr 2020', sideColX, sideY);
    sideY += 6;

    // Side Projects
    setFont('bold', 10);
    setColor('#1d1d1f');
    pdf.text('SIDE PROJECTS', sideColX, sideY);
    sideY += 5;

    setFont('bold', 7.5);
    setColor('#0066cc');
    pdf.textWithLink('ArcUp', sideColX, sideY, { url: 'https://apps.apple.com/il/app/arcup/id6747093775' });
    sideY += 3;
    setFont('normal', 6.5);
    setColor('#636366');
    const arcupDesc = wrapText('AI-powered tutor iOS app for software engineers with adaptive learning and gamified courses', sideColWidth);
    arcupDesc.forEach((line) => {
      pdf.text(line, sideColX, sideY);
      sideY += 2.5;
    });
    sideY += 3;

    // Languages
    setFont('bold', 10);
    setColor('#1d1d1f');
    pdf.text('LANGUAGES', sideColX, sideY);
    sideY += 5;

    setFont('normal', 7.5);
    setColor('#424245');
    pdf.text('English (Fluent) · Hebrew (Fluent) · Arabic (Native)', sideColX, sideY);

    pdf.save('yousef_nassar_resume.pdf');
  };

  const experiences = [
    {
      company: 'Melio',
      role: 'Tech Lead, Data & AI Infra Platform',
      period: '2026 — Present',
      achievements: [
        'Lead the Data & AI Infra Platform team, owning the platform foundations for agentic and data workloads',
        'Leading design and adoption of agentic infrastructure on AWS Bedrock AgentCore across engineering teams',
        'Driving observability for AI agents and services via OpenTelemetry instrumentation integrated with Datadog',
        'Leading data governance for business data enrichment and analytics pipelines',
      ],
    },
    {
      company: 'Infragate',
      role: 'Co-Founder',
      period: '2025 — Present',
      link: 'https://infragate.ai',
      achievements: [
        'Built ShareCube (sharecube.io), a publishing and collaboration service where AI agents publish docs over MCP',
        'ShareCube: selection-anchored comments, editable versioned artifacts, org roles, scoped API keys and search',
        'Built Cairn (cairn.infragate.co), an OpenTelemetry observability and evals platform for AI coding agents',
        'Cairn ingests all 3 OTLP signals via Rust Lambdas into a hot DynamoDB path and a cold Apache Iceberg lake',
        'Built LLM-as-judge evals: 5 built-in evaluators on Bedrock, benchmarked on a 59-unit frozen set',
        'Built no-ops platform to create and deploy serverless MCP servers entirely through UI, pay-as-you-go',
      ],
    },
    {
      company: 'Meta',
      role: 'Software Engineer',
      period: '2021 — 2025',
      achievements: [
        'Led ticket automation infrastructure reducing manual review by 65%, saving thousands of hours monthly',
        'Architected ML-powered recommendation systems for Instagram & Facebook Shops, achieving 25% engagement increase',
        'Spearheaded Similar Items & More from This Shop features with breakthrough ML models',
        'Conducted extensive A/B testing optimizing user engagement and conversion rates',
      ],
    },
    {
      company: 'Amazon',
      role: 'Software Engineer',
      period: '2019 — 2021',
      achievements: [
        'Designed end-to-end architecture and led the Browse Node Scoping project for Alexa Shopping',
        'Architected scalable infrastructure using AWS CDK with load balancers and Fargate',
        'Led cross-team technical initiatives establishing microservices best practices',
        'Drove architectural decisions, mentoring engineers on system design',
      ],
    },
    {
      company: 'GE Healthcare',
      role: 'Software Engineer',
      period: '2018 — 2019',
      achievements: [
        'Led backend architecture for AI assistant deployed in US hospitals',
        'Introduced AWS AppSync with GraphQL, improving API development efficiency',
        'Optimized AWS Lambda for improved scalability and reduced costs',
      ],
    },
    {
      company: 'Outbrain',
      role: 'Software Engineer',
      period: '2016 — 2018',
      achievements: [
        'Built real-time fraud detection reducing fraudulent events by 40%',
        'Processed millions of daily click events with Prometheus/Grafana monitoring',
        'Established CI/CD pipelines improving deployment frequency by 60%',
      ],
    },
    {
      company: 'Microsoft',
      role: 'Software Engineer Intern',
      period: '2014 — 2016',
      achievements: [
        'Built UI components for data visualization and backend retention policies for Azure',
      ],
    },
  ];

  return (
    <div className={`app ${isLoaded ? 'loaded' : ''}`}>
      <Analytics />

      <button className="download-btn" onClick={handlePrint} aria-label="Download PDF">
        <Download size={18} strokeWidth={1.5} />
      </button>

      <div className="container">
        {/* Header */}
        <header className="header">
          <h1 className="name">Yousef Nassar</h1>
          <p className="title">Software Engineer & Architect</p>
          <div className="contact">
            <a href="mailto:n.yossif@gmail.com">n.yossif@gmail.com</a>
            <span className="divider" />
            <a href="tel:+972505771645">+972 50 577 1645</a>
            <span className="divider" />
            <span>Haifa</span>
            <span className="divider" />
            <a href="https://ynassar.com" target="_blank" rel="noopener noreferrer">ynassar.com</a>
            <span className="divider" />
            <a href="https://www.linkedin.com/in/yousef-nassar-314a3b5b/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
          </div>
        </header>

        <div className="content">
          {/* Experience */}
          <section className="experience">
            <h2 className="section-title">Experience</h2>

            {experiences.map((exp, idx) => (
              <article
                key={idx}
                className="job"
                style={{ animationDelay: `${0.1 + idx * 0.08}s` }}
              >
                <div className="job-header">
                  <div className="job-meta">
                    <h3 className="company">
                      {exp.link ? (
                        <a href={exp.link} target="_blank" rel="noopener noreferrer">{exp.company}</a>
                      ) : (
                        exp.company
                      )}
                    </h3>
                    <span className="role">{exp.role}</span>
                  </div>
                  <span className="period">{exp.period}</span>
                </div>
                <ul className="achievements">
                  {exp.achievements.map((achievement, i) => (
                    <li key={i}>{achievement}</li>
                  ))}
                </ul>
              </article>
            ))}
          </section>

          {/* Sidebar */}
          <aside className="sidebar">
            {/* Skills */}
            <section className="skills">
              <h2 className="section-title">Expertise</h2>
              <div className="skill-group">
                <h4>Leadership</h4>
                <p>System Architecture, Technical Strategy, Cross-functional Collaboration</p>
              </div>
              <div className="skill-group">
                <h4>Backend</h4>
                <p>AWS, Microservices, Python, Java, Node.js</p>
              </div>
              <div className="skill-group">
                <h4>Frontend</h4>
                <p>React, React Native, TypeScript, Expo</p>
              </div>
              <div className="skill-group">
                <h4>AI & ML</h4>
                <p>ML Systems, NLP, Deep Learning, Model Deployment</p>
              </div>
              <div className="skill-group">
                <h4>DevOps</h4>
                <p>CI/CD, Docker, Kubernetes, Monitoring</p>
              </div>
            </section>

            {/* Education */}
            <section className="education">
              <h2 className="section-title">Education</h2>
              <div className="edu-item">
                <h4>BSc. Computer Science</h4>
                <p className="school">Technion — Israel Institute of Technology</p>
                <span className="year">2012 — 2017</span>
              </div>
            </section>

            {/* Certifications */}
            <section className="certifications">
              <h2 className="section-title">Certifications</h2>
              <ul>
                <li className="cert-specialization">
                  <a href="https://coursera.org/share/475756e9f3fe76ab11b67507ae5aa5c1" target="_blank" rel="noopener noreferrer">
                    Deep Learning Specialization
                  </a>
                  <span className="cert-provider">DeepLearning.AI · Coursera · September 2020</span>
                  <ul className="cert-courses">
                    <li>Neural Networks and Deep Learning</li>
                    <li>Improving Deep Neural Networks</li>
                    <li>Structuring Machine Learning Projects</li>
                    <li>Convolutional Neural Networks</li>
                    <li>Sequence Models</li>
                  </ul>
                </li>
                <li>
                  <a href="https://www.coursera.org/account/accomplishments/verify/SWGGZDT49ZZN" target="_blank" rel="noopener noreferrer">
                    NLP Specialization
                  </a>
                  <span className="cert-provider">DeepLearning.AI · Coursera · December 2020</span>
                </li>
                <li>
                  <a href="https://www.coursera.org/account/accomplishments/verify/9L8NP7Q7F2QZ" target="_blank" rel="noopener noreferrer">
                    Big Data Analysis with Scala and Spark
                  </a>
                  <span className="cert-provider">EPFL · Coursera · February 2020</span>
                </li>
                <li>
                  <a href="https://www.coursera.org/account/accomplishments/verify/KBL5C6Q3NXNG" target="_blank" rel="noopener noreferrer">
                    Functional Programming Principles in Scala
                  </a>
                  <span className="cert-provider">EPFL · Coursera · April 2020</span>
                </li>
              </ul>
            </section>

            {/* Side Projects */}
            <section className="side-projects">
              <h2 className="section-title">Side Projects</h2>
              <div className="project-item">
                <h4>
                  <a href="https://apps.apple.com/il/app/arcup/id6747093775" target="_blank" rel="noopener noreferrer">
                    ArcUp
                  </a>
                </h4>
                <p className="project-desc">AI-powered tutor iOS app for software engineers with adaptive learning, gamified courses, and personalized lessons</p>
              </div>
            </section>

            {/* Languages */}
            <section className="languages">
              <h2 className="section-title">Languages</h2>
              <div className="lang-list">
                <span>English</span>
                <span>Hebrew</span>
                <span>Arabic</span>
              </div>
            </section>
          </aside>
        </div>
      </div>

      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .app {
          min-height: 100vh;
          background: #fafafa;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif;
          color: #1d1d1f;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        /* Download Button */
        .download-btn {
          position: fixed;
          top: 32px;
          right: 32px;
          width: 48px;
          height: 48px;
          background: #1d1d1f;
          color: #fff;
          border: none;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 100;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
        }

        .download-btn:hover {
          transform: scale(1.05);
          box-shadow: 0 4px 30px rgba(0, 0, 0, 0.15);
        }

        .download-btn:active {
          transform: scale(0.98);
        }

        /* Container */
        .container {
          max-width: 1000px;
          margin: 0 auto;
          padding: 80px 48px 120px;
        }

        /* Header */
        .header {
          text-align: center;
          margin-bottom: 80px;
          opacity: 0;
          transform: translateY(20px);
          animation: fadeUp 0.8s ease forwards;
        }

        .loaded .header {
          opacity: 1;
          transform: translateY(0);
        }

        .name {
          font-size: 56px;
          font-weight: 600;
          letter-spacing: -1.5px;
          color: #1d1d1f;
          margin-bottom: 12px;
          line-height: 1.1;
        }

        .title {
          font-size: 21px;
          font-weight: 400;
          color: #86868b;
          margin-bottom: 32px;
          letter-spacing: -0.3px;
        }

        .contact {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
        }

        .contact a, .contact span {
          font-size: 14px;
          color: #1d1d1f;
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .contact a:hover {
          color: #0066cc;
        }

        .divider {
          width: 4px;
          height: 4px;
          background: #d2d2d7;
          border-radius: 50%;
        }

        /* Content Grid */
        .content {
          display: grid;
          grid-template-columns: 1fr 280px;
          gap: 80px;
        }

        /* Section Title */
        .section-title {
          font-size: 13px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #86868b;
          margin-bottom: 32px;
        }

        /* Experience */
        .job {
          margin-bottom: 48px;
          opacity: 0;
          transform: translateY(16px);
          animation: fadeUp 0.6s ease forwards;
        }

        .loaded .job {
          opacity: 1;
          transform: translateY(0);
        }

        .job:last-child {
          margin-bottom: 0;
        }

        .job-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
        }

        .company {
          font-size: 24px;
          font-weight: 600;
          letter-spacing: -0.5px;
          color: #1d1d1f;
          margin-bottom: 4px;
        }

        .company a {
          color: #1d1d1f;
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .company a:hover {
          color: #0066cc;
        }

        .role {
          font-size: 15px;
          color: #86868b;
        }

        .period {
          font-size: 14px;
          color: #86868b;
          white-space: nowrap;
          padding-top: 4px;
        }

        .achievements {
          list-style: none;
          padding: 0;
        }

        .achievements li {
          position: relative;
          padding-left: 20px;
          margin-bottom: 10px;
          font-size: 15px;
          line-height: 1.6;
          color: #424245;
        }

        .achievements li::before {
          content: '';
          position: absolute;
          left: 0;
          top: 10px;
          width: 5px;
          height: 5px;
          background: #0066cc;
          border-radius: 50%;
        }

        .achievements li:last-child {
          margin-bottom: 0;
        }

        /* Sidebar */
        .sidebar section {
          margin-bottom: 48px;
        }

        .sidebar section:last-child {
          margin-bottom: 0;
        }

        /* Skills */
        .skill-group {
          margin-bottom: 20px;
        }

        .skill-group:last-child {
          margin-bottom: 0;
        }

        .skill-group h4 {
          font-size: 14px;
          font-weight: 600;
          color: #1d1d1f;
          margin-bottom: 6px;
        }

        .skill-group p {
          font-size: 14px;
          color: #424245;
          line-height: 1.5;
        }

        /* Education */
        .edu-item h4 {
          font-size: 15px;
          font-weight: 600;
          color: #1d1d1f;
          margin-bottom: 4px;
        }

        .edu-item .school {
          font-size: 14px;
          color: #424245;
          margin-bottom: 4px;
        }

        .edu-item .year {
          font-size: 13px;
          color: #86868b;
        }

        /* Certifications */
        .certifications ul {
          list-style: none;
        }

        .certifications li {
          font-size: 14px;
          color: #424245;
          padding: 8px 0;
          border-bottom: 1px solid #f0f0f0;
        }

        .certifications li:last-child {
          border-bottom: none;
        }

        .certifications li a {
          color: #424245;
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .certifications li a:hover {
          color: #0066cc;
        }

        .cert-specialization {
          padding-bottom: 12px !important;
        }

        .cert-specialization > a {
          font-weight: 500;
          color: #1d1d1f;
        }

        .cert-provider {
          display: block;
          font-size: 12px;
          color: #86868b;
          margin-top: 2px;
        }

        .cert-courses {
          margin-top: 8px;
          padding-left: 0;
          list-style: none;
        }

        .cert-courses li {
          font-size: 13px;
          color: #636366;
          padding: 3px 0;
          padding-left: 12px;
          border-bottom: none;
          position: relative;
        }

        .cert-courses li::before {
          content: '·';
          position: absolute;
          left: 0;
          color: #86868b;
        }

        /* Side Projects */
        .project-item h4 {
          font-size: 15px;
          font-weight: 600;
          color: #1d1d1f;
          margin-bottom: 4px;
        }

        .project-item h4 a {
          color: #1d1d1f;
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .project-item h4 a:hover {
          color: #0066cc;
        }

        .project-desc {
          font-size: 13px;
          color: #636366;
          line-height: 1.5;
        }

        /* Languages */
        .lang-list {
          display: flex;
          gap: 24px;
        }

        .lang-list span {
          font-size: 14px;
          color: #424245;
        }

        /* Animations */
        @keyframes fadeUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Responsive */
        @media (max-width: 900px) {
          .content {
            grid-template-columns: 1fr;
            gap: 64px;
          }

          .container {
            padding: 60px 24px 100px;
          }

          .name {
            font-size: 40px;
          }

          .title {
            font-size: 18px;
          }

          .download-btn {
            top: auto;
            bottom: 24px;
            right: 24px;
          }
        }

        @media (max-width: 600px) {
          .name {
            font-size: 32px;
          }

          .company {
            font-size: 20px;
          }

          .job-header {
            flex-direction: column;
            gap: 8px;
          }

          .contact {
            flex-direction: column;
            gap: 8px;
          }

          .divider {
            display: none;
          }

          .header {
            margin-bottom: 48px;
          }

          .lang-list {
            flex-direction: column;
            gap: 8px;
          }
        }
      `}</style>
    </div>
  );
}

export default Resume;
